import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { useAppSelector } from '@reducers/index';
import { currentInitiative } from '@selectors/initiative';
import {
  useGetEmissionsByActivityTypeQuery,
  useGetEmissionsByMonthQuery,
  useGetEmissionsSummaryQuery,
  useGetIntensityMetricsQuery,
  useGetSettingsQuery,
  useGetTargetsQuery,
} from '../../api/emissions';
import {
  getCompletedFiscalYear,
  getFiscalYearOptions,
  getFiscalYearPeriod,
  getPreviousYearPeriod,
} from '../../components/FiscalYearSelector';
import { EmissionsOnePagerPreview, type OnePagerMode } from './EmissionsOnePagerPreview';
import { useGenerateOnePager } from './useGenerateOnePager';
import './one-pager-modal.scss';

interface EmissionsOnePagerModalProps {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  hasChildren: boolean;
}

const FISCAL_YEAR_OPTIONS = getFiscalYearOptions();

const FORMAT_OPTIONS: ReadonlyArray<{ value: OnePagerMode; label: string; icon: string }> = [
  { value: 'pdf', label: 'PDF (A4)', icon: 'fa-file-pdf' },
  { value: 'image', label: 'Image (1080×1080)', icon: 'fa-image' },
];

const PREVIEW_MAX_SCALE = 0.55;
const PREVIEW_FRAME_PADDING = 32;
const PREVIEW_DIMENSIONS: Record<OnePagerMode, { width: number; height: number }> = {
  pdf: { width: 800, height: 1131 },
  image: { width: 1080, height: 1080 },
};

const ErrorAlert = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={`one-pager-modal__alert alert alert-danger py-2 ${className ?? ''}`} role='alert'>
    <i className='fa fa-exclamation-circle me-2' />
    {children}
  </div>
);

export const EmissionsOnePagerModal = ({
  isOpen,
  toggle,
  initiativeId,
  hasChildren,
}: EmissionsOnePagerModalProps) => {
  const initiative = useAppSelector(currentInitiative);
  const [selectedYear, setSelectedYear] = useState<number>(getCompletedFiscalYear());
  const [mode, setMode] = useState<OnePagerMode>('pdf');

  const period = getFiscalYearPeriod(selectedYear);
  const priorPeriod = getPreviousYearPeriod(selectedYear);
  const skip = !isOpen || !initiativeId;

  const baseArgs = { initiativeId, includeChildren: hasChildren };
  const summaryArgs = { ...baseArgs, periodStart: period.start, periodEnd: period.end };
  const priorArgs = { ...baseArgs, periodStart: priorPeriod.start, periodEnd: priorPeriod.end };

  const summaryQuery = useGetEmissionsSummaryQuery(summaryArgs, { skip });
  const priorQuery = useGetEmissionsSummaryQuery(priorArgs, { skip });
  const hotspotsQuery = useGetEmissionsByActivityTypeQuery(summaryArgs, { skip });
  const intensityQuery = useGetIntensityMetricsQuery(summaryArgs, { skip });
  const settingsQuery = useGetSettingsQuery({ initiativeId }, { skip });
  const monthlyQuery = useGetEmissionsByMonthQuery(summaryArgs, { skip });
  const targetsQuery = useGetTargetsQuery({ initiativeId }, { skip });

  const hasBlockingError = summaryQuery.isError || settingsQuery.isError;
  const isAnyFetching =
    summaryQuery.isFetching ||
    priorQuery.isFetching ||
    hotspotsQuery.isFetching ||
    intensityQuery.isFetching ||
    settingsQuery.isFetching ||
    monthlyQuery.isFetching ||
    targetsQuery.isFetching;
  const isReady = Boolean(summaryQuery.data && !hasBlockingError && !isAnyFetching);

  const captureRef = useRef<HTMLDivElement>(null);
  const { generate, clearError, isExporting, error } = useGenerateOnePager();

  // Ref-callback (not useRef) so the observer attaches after reactstrap's
  // deferred body mount — a useRef + useEffect([]) misses the initial attach.
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  useEffect(() => {
    if (!frameEl) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) {
        setFrameWidth(entry.contentRect.width);
      }
    });
    observer.observe(frameEl);
    return () => observer.disconnect();
  }, [frameEl]);

  // reactstrap unmounts the body on close, so this initialiser runs fresh
  // each time the modal opens — the methodology stamp stays honest.
  const [generatedAt] = useState<Date>(() => new Date());

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    clearError();
  };
  const handleModeChange = (next: OnePagerMode) => {
    setMode(next);
    clearError();
  };

  const handleDownload = async () => {
    if (!captureRef.current || !initiative || !summaryQuery.data) {
      return;
    }
    await generate({
      mode,
      node: captureRef.current,
      initiativeId,
      initiativeName: initiative.name,
      period,
    });
  };

  const previewSize = PREVIEW_DIMENSIONS[mode];
  const availableWidth = Math.max(0, frameWidth - PREVIEW_FRAME_PADDING);
  const previewScale = frameWidth > 0
    ? Math.min(PREVIEW_MAX_SCALE, availableWidth / previewSize.width)
    : PREVIEW_MAX_SCALE;

  const previewProps = summaryQuery.data && {
    mode,
    initiativeName: initiative?.name ?? '',
    initiativeLogo: initiative?.profile,
    period,
    summary: summaryQuery.data,
    priorSummary: priorQuery.data,
    hotspots: hotspotsQuery.data,
    intensity: intensityQuery.data,
    settings: settingsQuery.data,
    monthly: monthlyQuery.data?.results,
    targets: targetsQuery.data?.data,
    generatedAt,
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size='lg'>
      <ModalHeader toggle={toggle}>Export Emissions One-Pager</ModalHeader>
      <ModalBody>
        <p className='text-ThemeTextLight mb-3'>
          A media-kit one-pager summarising your carbon footprint. Share with customers on your website
          or with investors in your data room.
        </p>

        <div className='d-flex gap-3 mb-3 align-items-end'>
          <div style={{ flex: '0 0 140px' }}>
            <label className='form-label fw-medium small' htmlFor='one-pager-reporting-year'>
              Reporting year
            </label>
            <select
              id='one-pager-reporting-year'
              className='form-select form-select-sm'
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
            >
              {FISCAL_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className='flex-grow-1'>
            <label className='form-label fw-medium small'>Format</label>
            <div className='btn-group w-100'>
              {FORMAT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type='button'
                  className={`btn btn-sm ${mode === opt.value ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => handleModeChange(opt.value)}
                >
                  <i className={`fa ${opt.icon} me-1`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {hasBlockingError ? (
          <ErrorAlert className='mb-3'>
            We couldn't load your emissions data. Please close this dialog and try again — if the problem
            persists, contact support.
          </ErrorAlert>
        ) : null}

        <div
          ref={setFrameEl}
          className='one-pager-modal__preview-frame'
          style={{ height: previewSize.height * previewScale + PREVIEW_FRAME_PADDING }}
        >
          {previewProps ? (
            <div
              className='one-pager-modal__preview-scaled'
              style={{
                width: previewSize.width,
                height: previewSize.height,
                transform: `scale(${previewScale})`,
              }}
              aria-hidden='true'
            >
              <EmissionsOnePagerPreview {...previewProps} />
            </div>
          ) : (
            <div className='text-ThemeTextLight' aria-busy='true' role='status'>
              {hasBlockingError ? 'Unable to load preview.' : 'Loading preview…'}
            </div>
          )}
        </div>

        {/* Off-screen unscaled copy: html2canvas captures this one so the scale
            transform on the visible copy can't affect layout measurement. */}
        {previewProps ? (
          <div className='one-pager-modal__capture' aria-hidden='true'>
            <EmissionsOnePagerPreview ref={captureRef} {...previewProps} />
          </div>
        ) : null}

        {error ? <ErrorAlert className='mt-3 mb-0'>{error}</ErrorAlert> : null}
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={toggle} disabled={isExporting}>
          Cancel
        </Button>
        <Button onClick={handleDownload} disabled={isExporting || !isReady}>
          {isExporting
            ? <><span className='spinner-border spinner-border-sm me-1' />Generating…</>
            : <><i className='fa fa-download me-1' />Download {mode === 'pdf' ? 'PDF' : 'image'}</>
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
};
